// Bilingual translations (EN / AR). Keep flat, namespaced keys.
export const translations = {
  en: {
    meta: {
      title: "FlowWhite | Premium Water Piping for Wellbeing",
      description:
        "Qatar's only licensed Lubrizol FlowGuard CPVC manufacturer. Premium water piping for stadiums, hospitals, and high-spec residences. Made in Qatar since 2015.",
    },
    nav: {
      about: "About",
      gcc: "GCC",
      factory: "Factory",
      compare: "CPVC vs PPR",
      projects: "Projects",
      certifications: "Certifications",
      vision: "Vision 2030",
      contact: "Contact",
      catalogue: "Download Catalogue",
    },
    hero: {
      kicker: "Qatar's only licensed Lubrizol FlowGuard CPVC manufacturer",
      title_1: "Premium Water Piping",
      title_2: "for Wellbeing.",
      subtitle:
        "Made in Qatar since 2015. Engineered to DIN EN ISO 15877. Trusted on stadiums, hospitals, hotels, and the highest-spec residential addresses in the country.",
      cta_primary: "Talk to our team",
      cta_secondary: "Download Technical Catalogue",
      stat_1: "6,000",
      stat_1_label: "sqm ISO-certified facility",
      stat_2: "100%",
      stat_2_label: "Qatari-owned manufacturer",
      stat_3: "Sole",
      stat_3_label: "Lubrizol FlowGuard licensee in Qatar",
      stat_4: "16–225",
      stat_4_label: "mm pipe range, hot & cold potable",
    },
    investor: {
      overline: "For Investors",
      title: "A defensible position in Qatar's manufacturing sector.",
      lead:
        "FlowWhite (formerly Al Waab Plastics Factory) is the exclusive Qatar licensee of Lubrizol Corporation's FlowGuard™ CPVC technology, the global benchmark for hot and cold potable water systems since 1960. Our position is structural, not promotional.",
      pillars: [
        {
          tag: "Exclusivity",
          title: "Sole licensee in the State of Qatar.",
          body: "FlowGuard is a US-engineered compound. Manufacturing licences are tightly controlled. We hold the only one in Qatar, independently verifiable with Lubrizol Corporation.",
        },
        {
          tag: "Tailwind",
          title: "Aligned to Qatar National Vision 2030.",
          body: "QCS 2014 codifies CPVC as a recommended material for potable water. Our products are EPD- and LCA-declared 'Green' and contribute to LEED & GSAS scoring on every project.",
        },
        {
          tag: "Substitution",
          title: "The structural shift from PPR to CPVC.",
          body: "MEP consultants in the GCC are migrating away from PPR for hot-water risers. CPVC delivers higher temperature ratings, chlorine-dioxide resistance, faster installation, and lifetime cost advantage.",
        },
        {
          tag: "Footprint",
          title: "Built for scale.",
          body: "6,000 m² ISO-certified plant in Doha. European processing technology: Theysohn extruders, Engel and Milacron injection moulding. Capacity headroom to serve GCC export demand.",
        },
      ],
      cta: "Request a private briefing",
    },
    gcc: {
      overline: "Our GCC Footprint",
      title: "CPVC manufacturing platform, anchored in the UAE and extended across the region.",
      leadPoints: [
        "FlowWhite holds the exclusive UAE manufacturing rights for FlowGuard CPVC, securing a protected production position at the center of the market.",
        "This control now extends beyond a single geography. The Al Waab facility in Qatar operates under the same management group, forming a unified manufacturing and supply structure across both markets.",
        "Together, this creates a coordinated regional platform for CPVC production and distribution, strengthening supply continuity, delivery capability, and market reach.",
      ],
      journey: [
        { tag: "Origin", label: "United States · Lubrizol FlowGuard™", year: "since 1959" },
        { tag: "Anchor", label: "Qatar · Al Waab", year: "since 2015" },
        { tag: "Expansion", label: "UAE · FlowWhite", year: "active 2026" },
      ],
      qatar: {
        tag: "Established · Doha, Qatar",
        stat: "100%",
        statLabel: "in operation",
        body:
          "Al Waab, our Qatar facility, has been in continuous operation since 2015 — the sole Lubrizol FlowGuard™ licensee in the State of Qatar, specified on stadiums, hospitals, and the country's most consequential addresses.",
        chip: "Fully operational",
      },
      uae: {
        tag: "Expanding · Dubai, UAE",
        stat: "50%",
        statLabel: "in operation",
        body:
          "FlowWhite UAE, our Dubai Investment Park expansion, is now 50% operational and progressing steadily toward full capacity, maintaining the same rigorous QC discipline established in Doha.",
        chip: "Ramping to full capacity",
      },
    },
    factory: {
      overline: "The Factory",
      title: "FlowGuard from the USA. Manufactured to international standards for the GCC.",
      body:
        "FlowWhite brings FlowGuard CPVC technology into the GCC through an aligned UAE and Qatar manufacturing structure, supported by USA-sourced compound and international production standards.\n\nOur platform connects FlowGuard material specification, certified manufacturing, and regional distribution into one controlled supply chain serving the UAE, Qatar, and wider GCC demand.",
      bullets: [
        "Lubrizol FlowGuard™ compound, sourced from USA",
        "Theysohn extruders, Engel and Milacron moulding",
        "DIN EN ISO 15877 / DIN 8079 / DIN 8080 conformance",
        "Lead-free, NSF-certified, WRAS-approved",
        "Range: 16 mm (½″) to 225 mm (9″) metric series",
        "Solvent-cement jointing for high strength at elevated T & P",
      ],
    },
    compare: {
      overline: "For MEP Consultants",
      title: "Why specifying CPVC over PPR is the defensible choice.",
      lead: "A side-by-side technical comparison for hot and cold potable water risers in GCC climate conditions.",
      cpvc_label: "FlowWhite CPVC",
      ppr_label: "PPR",
      rows: [
        { metric: "Maximum operating temperature", cpvc: "93 °C continuous", ppr: "70 °C continuous" },
        { metric: "Chlorine / Chlorine-dioxide resistance", cpvc: "Excellent. Designed for treated potable water", ppr: "Susceptible to oxidative degradation" },
        { metric: "Joint method", cpvc: "Solvent cement, chemical fusion", ppr: "Heat fusion, site-skill dependent" },
        { metric: "Pressure rating at 82 °C", cpvc: "Maintains rated pressure", ppr: "Significant de-rating required" },
        { metric: "Service life expectancy", cpvc: "50+ years (FM/NSF tested)", ppr: "Material-dependent" },
        { metric: "QCS 2014 conformance", cpvc: "Specified", ppr: "Limited" },
        { metric: "Fire performance", cpvc: "Self-extinguishing, low smoke", ppr: "Combustible" },
      ],
      footnote: "Sources: Lubrizol Corporation FlowGuard™ technical bulletins, DIN EN ISO 15877, QCS 2014.",
    },
    projects: {
      overline: "Iconic Projects",
      title: "Specified on the country's most consequential addresses.",
      lead: "From World Cup stadiums to the residences of Lusail Marina, FlowWhite CPVC has been installed on the projects that define modern Qatar.",
    },
    certs: {
      overline: "Credentials",
      title: "Independently verified. Internationally recognised.",
      items: [
        { name: "Lubrizol FlowGuard™ Licensee", body: "Sole authorised manufacturer in the State of Qatar." },
        { name: "NSF / ANSI 61", body: "Certified safe for direct drinking-water contact." },
        { name: "WRAS Approved", body: "UK Water Regulations Advisory Scheme: material approval." },
        { name: "ISO 9001:2015", body: "Quality management, independently audited." },
        { name: "DIN EN ISO 15877", body: "European standard for CPVC plumbing systems." },
        { name: "QCS 2014", body: "Qatar Construction Specification, compliant for potable water." },
      ],
    },
    vision: {
      overline: "Qatar National Vision 2030",
      title: "We will meet the needs of this generation without compromising the next.",
      body:
        "Our products are EPD- and LCA-declared green products. Lead-free CPVC contributes to LEED and GSAS rating points across material safety, embodied energy, and water-system longevity, directly supporting the environmental pillar of QNV-2030.",
    },
    partners: {
      overline: "Industrial Associates",
      title: "In partnership with Lubrizol Corporation, USA.",
      body: "Lubrizol Corporation invented FlowGuard™ CPVC technology in 1959. We manufacture under their direct compound supply and processing technology partnership.",
    },
    contact: {
      overline: "Contact",
      title_investor: "Considering an investment? Let's talk privately.",
      title_mep: "Specifying for an upcoming project?",
      lead:
        "For investor enquiries, partnership discussions, or technical specification support. Leave a note and we will respond within one business day.",
      labels: {
        name: "Full name",
        email: "Email",
        mobile: "Mobile (optional)",
        company: "Company",
        role: "I am a",
        role_options: ["Investor / Family Office", "MEP Consultant", "Procurement / Contractor", "Press / Other"],
        message: "Message (optional)",
        send: "Send message",
        sending: "Sending…",
        sent: "Message sent. We will respond within one business day.",
        error: "Sending failed. Please try again or email us directly.",
      },
      address: {
        title: "Head Office",
        line: "Investment Park, Dubai, UAE",
        phone_label: "Phone",
        phone: "+971 4 313 2435",
        mobile_label: "Mobile",
        mobile: "+971 50 504 3000",
        email_label: "Email",
      },
    },
    footer: {
      tagline: "Premium water piping for wellbeing. Made in Qatar.",
      rights: "All rights reserved.",
      legal_name: "FlowWhite, operated by Al Waab Plastics Factory W.L.L.",
      links_title: "Site",
      contact_title: "Contact",
      legal_title: "Legal",
      privacy: "Privacy",
      terms: "Terms",
    },
    booth: {
      kicker: "MIITE • Booth Intake",
      title: "Stay in touch with FlowWhite.",
      subtitle:
        "Leave your details and our team will follow up after the event with the materials and conversation that fit your interest.",
      labels: {
        name: "Full name",
        email: "Email",
        mobile: "Mobile",
        company: "Company",
        role: "I am a",
        role_options: ["Buyer", "Trader", "Investor", "Partner", "Other"],
        message: "Message (optional)",
        send: "Submit",
        sending: "Submitting…",
        sent: "Thanks — we have your details. Our team will follow up shortly.",
        reset: "Start a new entry",
        error: "Submission failed. Please try again.",
        retry: "Try again",
      },
    },
    common: {
      lang_toggle_to_ar: "العربية",
      lang_toggle_to_en: "English",
      learn_more: "Learn more",
      back_to_top: "Back to top",
    },
  },
  ar: {
    meta: {
      title: "فلو وايت | أنابيب مياه فاخرة من أجل الرفاهية",
      description:
        "المصنع الوحيد المرخّص في قطر لأنابيب لوبريزول فلوغارد CPVC. أنابيب مياه فاخرة للملاعب والمستشفيات والعناوين السكنية الراقية. صُنع في قطر منذ 2015.",
    },
    nav: {
      about: "نبذة",
      gcc: "دول الخليج",
      factory: "المصنع",
      compare: "CPVC مقابل PPR",
      projects: "المشاريع",
      certifications: "الشهادات",
      vision: "رؤية 2030",
      contact: "تواصل معنا",
      catalogue: "تحميل الكتالوج",
    },
    hero: {
      kicker: "المصنع الوحيد المرخّص في قطر لأنابيب لوبريزول فلوغارد CPVC",
      title_1: "أنابيب مياه فاخرة",
      title_2: "من أجل الرفاهية.",
      subtitle:
        "صُنع في قطر منذ عام 2015 وفق المواصفة الأوروبية DIN EN ISO 15877. موثوق به في الملاعب والمستشفيات والفنادق وأرقى العناوين السكنية في الدولة.",
      cta_primary: "تواصل مع فريقنا",
      cta_secondary: "تحميل الكتالوج التقني",
      stat_1: "6,000",
      stat_1_label: "م² منشأة بمعايير ISO",
      stat_2: "100%",
      stat_2_label: "ملكية قطرية كاملة",
      stat_3: "الوحيد",
      stat_3_label: "المرخّص لتصنيع FlowGuard في قطر",
      stat_4: "16–225",
      stat_4_label: "ملم، مياه ساخنة وباردة للشرب",
    },
    investor: {
      overline: "للمستثمرين",
      title: "موقع تنافسي محصّن في قطاع التصنيع القطري.",
      lead:
        "فلو وايت (سابقًا مصنع الوعب للبلاستيك) هي الجهة الحصرية المرخّصة في قطر من شركة لوبريزول الأمريكية لتقنية FlowGuard™ من CPVC، المعيار العالمي لأنظمة مياه الشرب الساخنة والباردة منذ عام 1960. موقعنا هيكلي وليس ترويجيًا.",
      pillars: [
        {
          tag: "حصرية",
          title: "المرخَّص الوحيد في دولة قطر.",
          body: "تقنية FlowGuard ملكية أمريكية ورخص التصنيع محدودة جدًا. نحملها وحدنا في قطر، ويمكن التحقق من ذلك مباشرةً مع شركة لوبريزول.",
        },
        {
          tag: "محرّك سوقي",
          title: "متوافق مع رؤية قطر الوطنية 2030.",
          body: "تنصّ مواصفات البناء القطرية QCS 2014 على CPVC كمادة موصى بها لمياه الشرب. منتجاتنا حاملة لإعلانات EPD و LCA كمنتجات خضراء وتساهم في تقييم LEED و GSAS.",
        },
        {
          tag: "تحوّل تقني",
          title: "التحوّل الهيكلي من PPR إلى CPVC.",
          body: "استشاريو الميكانيكا في الخليج يتجهون لاستبدال PPR في خطوط المياه الساخنة. الـ CPVC يقدّم تحمّلًا حراريًا أعلى ومقاومة لثاني أكسيد الكلور وتركيبًا أسرع وكلفة أقل على عمر المبنى.",
        },
        {
          tag: "البنية الإنتاجية",
          title: "مبني للتوسّع.",
          body: "مصنع 6,000 م² بشهادة ISO في الدوحة، خطوط بثق ثيسون وحقن إنجل وميلاكرون بشراكة تقنية مباشرة مع لوبريزول. طاقة فائضة لخدمة الطلب الخليجي للتصدير.",
        },
      ],
      cta: "اطلب جلسة إحاطة خاصة",
    },
    gcc: {
      overline: "حضورنا في دول الخليج",
      title: "منصّة تصنيع CPVC، مرتكزها الإمارات وامتدادها عبر المنطقة.",
      leadPoints: [
        "تمتلك FlowWhite حقوق التصنيع الحصرية في الإمارات لمادة FlowGuard CPVC، مما يؤمّن لها موقعاً إنتاجياً محمياً في قلب السوق.",
        "هذا الامتلاك يتجاوز حدود جغرافية واحدة؛ إذ يعمل مصنع الوعب في قطر تحت إدارة المجموعة ذاتها، ليُشكّلا معاً منظومة تصنيع وتوريد موحّدة عبر السوقين.",
        "وبذلك تتشكّل منصّة إقليمية متناسقة لإنتاج وتوزيع CPVC، تعزّز استمرارية الإمداد، وقدرة التسليم، واتساع الانتشار في السوق.",
      ],
      journey: [
        { tag: "المصدر", label: "الولايات المتحدة · Lubrizol FlowGuard™", year: "منذ 1959" },
        { tag: "المرتكز", label: "قطر · الوعب", year: "منذ 2015" },
        { tag: "التوسّع", label: "الإمارات · FlowWhite", year: "بدء التشغيل 2026" },
      ],
      qatar: {
        tag: "قائم · الدوحة، قطر",
        stat: "100%",
        statLabel: "قيد التشغيل",
        body:
          "مصنع الوعب، منشأتنا في قطر، يعمل بشكل متواصل منذ 2015 — وهو المرخّص الوحيد من Lubrizol FlowGuard™ في دولة قطر، ومحدّد في الملاعب والمستشفيات وأرقى عناوين الدولة.",
        chip: "تشغيل كامل",
      },
      uae: {
        tag: "في توسّع · دبي، الإمارات",
        stat: "50%",
        statLabel: "قيد التشغيل",
        body:
          "FlowWhite الإمارات، توسّعنا في مجمع دبي للاستثمار، تعمل اليوم بنسبة 50% وتتقدّم بثبات نحو الطاقة الكاملة، مع المحافظة على نفس انضباط الجودة الصارم المعتمد في الدوحة.",
        chip: "تتدرّج نحو الطاقة الكاملة",
      },
    },
    factory: {
      overline: "المصنع",
      title: "FlowGuard من الولايات المتحدة. مُصنَّع وفق المعايير الدولية لخدمة دول الخليج.",
      body:
        "تُدخل FlowWhite تقنية FlowGuard CPVC إلى دول الخليج عبر منظومة تصنيع متكاملة بين الإمارات وقطر، مدعومة بمادة خام مصدرها الولايات المتحدة وبمعايير إنتاج دولية.\n\nتربط منصّتنا بين مواصفات مادة FlowGuard، والتصنيع المعتمد، والتوزيع الإقليمي ضمن سلسلة إمداد واحدة محكومة، تخدم الإمارات وقطر وسائر أسواق الخليج.",
      bullets: [
        "مادة Lubrizol FlowGuard™، مصدرها الولايات المتحدة",
        "خطوط بثق Theysohn وحقن Engel و Milacron",
        "مطابقة DIN EN ISO 15877 / DIN 8079 / DIN 8080",
        "خالية من الرصاص، معتمدة NSF و WRAS",
        "المقاسات: من 16 ملم (½ بوصة) حتى 225 ملم (9 بوصة)",
        "وصلات بالمذيب الكيميائي لأعلى متانة عند الحرارة والضغط",
      ],
    },
    compare: {
      overline: "لاستشاريي الميكانيكا",
      title: "لماذا CPVC هو الاختيار التقني الأمثل بدل PPR.",
      lead: "مقارنة تقنية مباشرة لخطوط مياه الشرب الساخنة والباردة في ظروف مناخ الخليج.",
      cpvc_label: "FlowWhite CPVC",
      ppr_label: "PPR",
      rows: [
        { metric: "أقصى درجة تشغيل", cpvc: "93°م مستمرة", ppr: "70°م مستمرة" },
        { metric: "مقاومة الكلور / ثاني أكسيد الكلور", cpvc: "ممتازة. مصمّم لمياه الشرب المعالجة", ppr: "عُرضة للتآكل التأكسدي" },
        { metric: "طريقة الوصل", cpvc: "مذيب كيميائي، انصهار جزيئي", cpvc_alt: "", ppr: "لحام حراري، يعتمد على مهارة الفنّي" },
        { metric: "الضغط عند 82°م", cpvc: "يحافظ على الضغط الاسمي", ppr: "يتطلب تخفيضًا كبيرًا في الضغط" },
        { metric: "العمر التشغيلي المتوقّع", cpvc: "أكثر من 50 عامًا (اختبار FM/NSF)", ppr: "يعتمد على المادة" },
        { metric: "مطابقة QCS 2014", cpvc: "محدّد", ppr: "محدود" },
        { metric: "الأداء عند الحريق", cpvc: "ذاتي الإطفاء، دخان منخفض", ppr: "قابل للاحتراق" },
      ],
      footnote: "المصادر: نشرات Lubrizol FlowGuard™ التقنية، DIN EN ISO 15877، QCS 2014.",
    },
    projects: {
      overline: "مشاريع أيقونية",
      title: "تم تحديده في أهم العناوين في الدولة.",
      lead: "من ملاعب كأس العالم إلى أرقى السكنات في مرسى لوسيل، رُكّبت أنابيب FlowWhite CPVC في المشاريع التي تعرّف قطر الحديثة.",
    },
    certs: {
      overline: "الاعتمادات",
      title: "تم التحقق منها مستقلاًا. ومُعترف بها دوليًا.",
      items: [
        { name: "ترخيص Lubrizol FlowGuard™", body: "المصنّع الوحيد المرخّص في دولة قطر." },
        { name: "NSF / ANSI 61", body: "معتمد لتلامس مياه الشرب." },
        { name: "WRAS Approved", body: "اعتماد المواد البريطاني للوائح المياه." },
        { name: "ISO 9001:2015", body: "إدارة الجودة، تدقيق مستقل." },
        { name: "DIN EN ISO 15877", body: "المعيار الأوروبي لأنظمة CPVC." },
        { name: "QCS 2014", body: "مطابق لمواصفات البناء القطرية لمياه الشرب." },
      ],
    },
    vision: {
      overline: "رؤية قطر الوطنية 2030",
      title: "نلبّي احتياجات هذا الجيل دون المساس باحتياجات الأجيال القادمة.",
      body:
        "منتجاتنا حاملة لإعلانات EPD و LCA كمنتجات خضراء. الـ CPVC الخالي من الرصاص يساهم في نقاط LEED و GSAS عبر سلامة المواد والطاقة المُجسّدة وعمر شبكة المياه، ويدعم مباشرةً الركيزة البيئية لرؤية 2030.",
    },
    partners: {
      overline: "شركاء صناعيون",
      title: "بالشراكة مع Lubrizol Corporation، الولايات المتحدة.",
      body: "ابتكرت لوبريزول تقنية FlowGuard™ من CPVC عام 1959. نصنع تحت توريد مباشر للمادة الخام وشراكة تقنية معهم.",
    },
    contact: {
      overline: "تواصل معنا",
      title_investor: "تفكّر في الاستثمار؟ لنتحدّث بسرّية.",
      title_mep: "تحدد المواصفات لمشروع قادم؟",
      lead:
        "لاستفسارات المستثمرين أو محادثات الشراكة أو الدعم التقني للمواصفات. اترك ملاحظة وسنعود إليك خلال يوم عمل واحد.",
      labels: {
        name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        mobile: "الجوال (اختياري)",
        company: "الشركة",
        role: "أنا",
        role_options: ["مستثمر / مكتب عائلي", "استشاري ميكانيكي", "مشتريات / مقاول", "صحافة / آخر"],
        message: "الرسالة (اختياري)",
        send: "إرسال الرسالة",
        sending: "جاري الإرسال…",
        sent: "تم إرسال رسالتك. سنعود إليك خلال يوم عمل واحد.",
        error: "فشل الإرسال. يرجى المحاولة مجددًا أو مراسلتنا مباشرة.",
      },
      address: {
        title: "المقر الرئيسي",
        line: "مجمع دبي للاستثمار، دبي، الإمارات",
        phone_label: "الهاتف",
        phone: "+971 4 313 2435",
        mobile_label: "الجوال",
        mobile: "+971 50 504 3000",
        email_label: "البريد الإلكتروني",
      },
    },
    footer: {
      tagline: "أنابيب مياه فاخرة من أجل الرفاهية. صُنع في قطر.",
      rights: "جميع الحقوق محفوظة.",
      legal_name: "FlowWhite، تشغيل مصنع الوعب للبلاستيك ذ.م.م.",
      links_title: "الموقع",
      contact_title: "اتصل",
      legal_title: "قانوني",
      privacy: "الخصوصية",
      terms: "الشروط",
    },
    booth: {
      kicker: "MIITE • تسجيل الزوّار",
      title: "ابقَ على تواصل مع فلو وايت.",
      subtitle:
        "اترك بياناتك وسيتواصل فريقنا بعد الفعالية بما يناسب اهتمامك من معلومات وحوار.",
      labels: {
        name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        mobile: "الجوال",
        company: "الشركة",
        role: "أنا",
        role_options: ["مشترٍ", "تاجر", "مستثمر", "شريك", "آخر"],
        message: "الرسالة (اختياري)",
        send: "إرسال",
        sending: "جاري الإرسال…",
        sent: "شكرًا — وصلتنا بياناتك. سيتواصل معك فريقنا قريبًا.",
        reset: "تسجيل جديد",
        error: "فشل الإرسال. يرجى المحاولة مجددًا.",
        retry: "حاول مرة أخرى",
      },
    },
    common: {
      lang_toggle_to_ar: "العربية",
      lang_toggle_to_en: "English",
      learn_more: "اعرف أكثر",
      back_to_top: "العودة للأعلى",
    },
  },
};

// Static data — same in both languages
export const projectsData = [
  { name_en: "Doha Marriott", name_ar: "فندق الدوحة ماريوت", img: "https://www.alwaabplastics.com/images/project/doha_marriot.jpg" },
  { name_en: "Doha Expo", name_ar: "إكسبو الدوحة", img: "https://www.alwaabplastics.com/images/project/doha_expo.jpg" },
  { name_en: "Ashghal Health Centres", name_ar: "مراكز أشغال الصحية", img: "https://www.alwaabplastics.com/images/project/ashgal_health.jpg" },
  { name_en: "Bus Station", name_ar: "محطة الحافلات", img: "https://www.alwaabplastics.com/images/project/bus_station.jpg" },
  { name_en: "Gewan Island", name_ar: "جزيرة قطيفان", img: "https://www.alwaabplastics.com/images/project/_gewan_island.jpg" },
  { name_en: "La Plage, South Pearl", name_ar: "لا بلاج، اللؤلؤة الجنوبية", img: "https://www.alwaabplastics.com/images/project/_la_plage_south_pearl_qatar.jpg" },
  { name_en: "National Health Lab", name_ar: "المختبر الوطني الصحي", img: "https://www.alwaabplastics.com/images/project/nationa_health_lab.jpg" },
  { name_en: "Katara Tower, Lusail Marina", name_ar: "برج كتارا، مرسى لوسيل", img: "https://www.alwaabplastics.com/images/project/lusail-marina.jpeg" },
  { name_en: "Qatar Foundation Stadium", name_ar: "ملعب مؤسسة قطر", img: "https://www.alwaabplastics.com/images/project/qatar-foundation-stadium.jpeg" },
  { name_en: "Al Rayyan Stadium", name_ar: "ملعب الريّان", img: "https://www.alwaabplastics.com/images/project/al-rayyan-stadium.jpeg" },
  { name_en: "3170 Affordable Villa", name_ar: "3170 فيلا اقتصادية", img: "https://www.alwaabplastics.com/images/project/37o-villa.jpeg" },
  { name_en: "Health Centre, Mesaieed", name_ar: "مركز مسيعيد الصحي", img: "https://www.alwaabplastics.com/images/project/health-center-mesaeed.jpeg" },
  { name_en: "Al Maha Center", name_ar: "مركز المها", img: "https://www.alwaabplastics.com/images/project/al-maha-center.jpeg" },
  { name_en: "ISF Duhail Camp", name_ar: "معسكر الدحيل ISF", img: "https://www.alwaabplastics.com/images/project/isf-duhail-camp.jpeg" },
  { name_en: "Residential Buildings, Fox Hills", name_ar: "مباني فوكس هيلز السكنية", img: "https://www.alwaabplastics.com/images/project/Residential-Buildings--Fox-Hills.jpeg" },
  { name_en: "Kahramaa Customer Centre, Thumama", name_ar: "مركز خدمات كهرماء، الثمامة", img: "https://www.alwaabplastics.com/images/project/Kahramaa-Customer-Centre-Thumama.jpeg" },
  { name_en: "Mega Reservoir, Umm Salal", name_ar: "خزان أم صلال الكبير", img: "https://www.alwaabplastics.com/images/project/Mega-Reservoir--Umm-Salal.jpeg" },
  { name_en: "Al Furjan Markets", name_ar: "أسواق الفرجان", img: "https://www.alwaabplastics.com/images/project/Al-Furjan-Markets.jpeg" },
];
